import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileText } from 'lucide-react';
import useProjectStore from '../../store/projectStore';
import useUIStore from '../../store/uiStore';
import { getProjectDesignSummary, downloadProjectDesignSummaryPdf } from '../../services/designService';
import { downloadBlob } from '../../utils/downloadFile';

const statusColors = {
  draft: 'bg-gray-200 text-gray-800',
  pending_review: 'bg-amber-100 text-amber-800',
  engineer_approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const ProjectDesignSummaryPage = () => {
  const { projects, fetchProjects } = useProjectStore();
  const showToast = useUIStore((s) => s.showToast);

  const [projectId, setProjectId] = useState('');
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!projectId) {
      setSummary(null);
      return;
    }
    setIsLoading(true);
    getProjectDesignSummary(projectId, approvedOnly)
      .then((res) => setSummary(res.data))
      .catch((err) => {
        showToast(err.response?.data?.message || 'Failed to load design summary', 'error');
        setSummary(null);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, approvedOnly]);

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const res = await downloadProjectDesignSummaryPdf(projectId, approvedOnly);
      downloadBlob(res.data, `project_${projectId}_design_summary.pdf`);
    } catch (err) {
      showToast('Failed to export PDF', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white">Project Design Summary</h1>
          <Link to="/design" className="text-sm font-semibold text-white hover:underline">
            ← Back to Calculators
          </Link>
        </div>

        <p className="text-sm text-gray-500 mb-6 max-w-3xl">
          Rolls up every design calculation saved against a project — pipe sizing, tank design,
          heat exchangers — onto one page. Pulls only what's already been calculated and saved;
          it does not compute anything new.
        </p>

        <div className="glass-card p-5 mb-6 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1">Project</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">Select a project…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
            <input
              type="checkbox"
              checked={approvedOnly}
              onChange={(e) => setApprovedOnly(e.target.checked)}
              className="rounded border-gray-300"
            />
            Engineer-approved only
          </label>

          <button
            onClick={handleDownloadPdf}
            disabled={!projectId || isDownloading}
            className="flex items-center gap-2 bg-komal-blue hover:bg-komal-blue-dark disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded"
          >
            <Download size={16} />
            {isDownloading ? 'Preparing…' : 'Download PDF'}
          </button>
        </div>

        {!projectId ? (
          <div className="glass-card p-8 text-center text-sm text-gray-500">
            Select a project above to see its design summary.
          </div>
        ) : isLoading ? (
          <div className="glass-card p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : !summary ? null : (
          <>
            <div className="glass-card p-5 mb-6">
              <h2 className="text-lg font-bold text-gray-900">{summary.project.name}</h2>
              <p className="text-sm text-gray-500">
                {summary.project.client_name || '—'} · {summary.project.location || '—'}
              </p>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <span className="text-gray-600">
                  Showing <strong>{summary.count}</strong> of <strong>{summary.total_count}</strong> calculation(s)
                </span>
                {summary.total_count > 0 && (
                  <span
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      summary.all_approved ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {summary.all_approved ? 'All items engineer-approved' : 'Not all items are engineer-approved'}
                  </span>
                )}
              </div>
            </div>

            {summary.calculations.length === 0 ? (
              <div className="glass-card p-8 text-center text-sm text-gray-500">
                {summary.total_count === 0
                  ? 'No design calculations are linked to this project yet.'
                  : 'No engineer-approved calculations yet for this project.'}
              </div>
            ) : (
              <div className="space-y-4">
                {summary.calculations.map((c) => (
                  <div key={c.id} className="glass-card p-5">
                    <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <FileText size={18} className="text-komal-blue" />
                        <h3 className="font-semibold text-gray-900 capitalize">
                          {c.calculator_type.replace(/_/g, ' ')}
                        </h3>
                        <span className="text-xs text-gray-400">#{c.id}</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${statusColors[c.status]}`}>
                        {c.status.replace(/_/g, ' ')}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                      {Object.entries(c.headline).map(([label, value]) => (
                        <div key={label}>
                          <p className="text-xs text-gray-400">{label}</p>
                          <p className="text-sm font-semibold text-gray-900">{value ?? '—'}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>
                        Created by {c.created_by_name || '—'}
                        {c.reviewed_by_name && ` · Reviewed by ${c.reviewed_by_name}`}
                        {c.created_at && ` · ${new Date(c.created_at).toLocaleDateString()}`}
                      </span>
                      <Link to={`/design/calculations/${c.id}`} className="font-semibold text-komal-blue hover:underline">
                        View full calculation →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectDesignSummaryPage;
